import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Heading,
  Icon,
  InlineStack,
  Link,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalFooter,
  Panel,
  PanelActions,
  PanelFooter,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Stack,
  Strong,
  Toggle,
} from '@customerio/pluma-components/react';
import { LATEST_SCHEMA_VERSION } from '@/services/db/warehouse-sync/entity';
import {
  getNewlyAvailableOptions,
  type WarehouseDataOption,
  type WarehouseDataOptionID,
  type WarehouseDataOptionTerms,
} from '@/features/warehouse-sync';

const CHANGELOG_URL = 'https://docs.customer.io/integrations/data-out/data-warehouses/data-warehouses-intro/#changelog';

export type SchemaUpgradeSettings = Partial<Record<WarehouseDataOptionID, boolean>>;

type UpdateSchemaVersionProps = {
  version: number;
  /** Lifted so a details row's "Upgrade sync" link can open the same modal. */
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isReadOnly: boolean;
  isTwilioEnabled: boolean;
  isUpgrading: boolean;
  terms: WarehouseDataOptionTerms;
  onUpgrade: (settings: SchemaUpgradeSettings) => void;
};

function panelCopy(version: number): string {
  if (version === 1) {
    return 'Upgrade to the latest export schema version to export Subjects, Outputs, Deliveries (with transactional messages) and Delivery Contents data.';
  }
  if (version < 4) {
    return 'Upgrade to the latest export schema version to export Delivery Contents data.';
  }
  return `Your sync is on export schema version ${version}. Upgrade to version ${LATEST_SCHEMA_VERSION} to receive the fields added since then.`;
}

/** The upgrade prompt, plus the modal that confirms it. */
export function UpdateSchemaVersion({
  version,
  isOpen,
  onOpenChange,
  isReadOnly,
  isTwilioEnabled,
  isUpgrading,
  terms,
  onUpgrade,
}: UpdateSchemaVersionProps) {
  // Memoised because the seeding effect below depends on it: a fresh array each
  // render would re-run that effect on every render.
  const availableOptions: WarehouseDataOption[] = useMemo(
    () => getNewlyAvailableOptions({ version, isTwilioEnabled, terms }),
    [version, isTwilioEnabled, terms],
  );
  const [hasConfirmed, setHasConfirmed] = useState(false);
  // Seeded once, all-on: the newly-available exports are opt-out, not opt-in.
  const [settings, setSettings] = useState<SchemaUpgradeSettings>(() =>
    Object.fromEntries(availableOptions.map((option) => [option.id, true])),
  );

  // `isTwilioEnabled` resolves from a query, so `inbound` can join the list after the first
  // render. Without this it would sit off while every other new export is on.
  useEffect(() => {
    setSettings((current) => {
      const missing = availableOptions.filter((option) => current[option.id] === undefined);
      if (missing.length === 0) return current;
      return { ...current, ...Object.fromEntries(missing.map((option) => [option.id, true])) };
    });
  }, [availableOptions]);

  const close = () => {
    setHasConfirmed(false);
    onOpenChange(false);
  };

  return (
    <>
      <Panel>
        <PanelHeader>
          <PanelTitle level="3">
            <InlineStack alignBlock="center" gap="050">
              <Icon name="tip" size="sm" color="base" />
              Upgrade sync schema
            </InlineStack>
          </PanelTitle>
        </PanelHeader>
        <PanelSection>
          <Paragraph variant="secondary" sizeVariant="xs">
            {panelCopy(version)}
          </Paragraph>
        </PanelSection>
        <PanelFooter>
          <PanelActions>
            <Button data-test-warehouse-button="upgrade-schema" variant="primary" onClick={() => onOpenChange(true)}>
              Upgrade schema version
            </Button>
          </PanelActions>
        </PanelFooter>
      </Panel>

      <Modal
        data-test-warehouse-modal="upgrade-schema"
        title="Upgrade your export sync schema"
        isOpen={isOpen}
        onClose={close}
      >
        <ModalBody>
          <List styleType="decimal" px="200" gap="200" mb="150">
            {availableOptions.length > 0 ? (
              <ListItem>
                <Heading level="4" mb="100">
                  Select which new files you want to export:
                </Heading>
                <Stack gap="150">
                  {availableOptions.map((option) => (
                    <InlineStack
                      key={option.id}
                      data-test-warehouse-upgrade-schema-option={option.id}
                      alignInline="space-between"
                      gap="200"
                    >
                      <Stack>
                        <Strong data-test-warehouse-upgrade-schema-option-name>{option.name}</Strong>
                        <Paragraph variant="secondary" sizeVariant="xs">
                          {option.description}
                        </Paragraph>
                      </Stack>
                      <Toggle
                        data-test-warehouse-upgrade-schema-option-switch
                        ariaLabel={option.name}
                        isChecked={settings[option.id] === true}
                        onChange={() =>
                          setSettings((current) => ({ ...current, [option.id]: current[option.id] !== true }))
                        }
                        isDisabled={isReadOnly}
                        size="sm"
                      />
                    </InlineStack>
                  ))}
                </Stack>
              </ListItem>
            ) : null}

            <ListItem>
              <Heading level="4" mb="100">
                Update your ingestion logic and existing tables
              </Heading>
              <Paragraph mb="100">
                Upgrading may require changes to file names, supporting new fields and updates to existing tables for
                compatibility. View the full list of changes in our documentation.
              </Paragraph>
              <Paragraph mb="100">
                New fields are populated after the upgrade. Historical rows are not backfilled; only a full reconnect
                re-streams your history.
              </Paragraph>
              <Link variant="secondary" isExternal={true} icon="exit" iconPosition="trailing" href={CHANGELOG_URL}>
                View documentation
              </Link>
            </ListItem>
          </List>

          <Checkbox
            data-test-warehouse-checkbox="confirm-update"
            label="Yes, I've made all changes and am ready to upgrade."
            isChecked={hasConfirmed}
            onCheckedChange={() => setHasConfirmed((current) => !current)}
          />
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            data-test-warehouse-button="confirm-upgrade-schema"
            variant="primary"
            isDisabled={isReadOnly || !hasConfirmed}
            isLoading={isUpgrading}
            onClick={() => onUpgrade(settings)}
          >
            Upgrade sync
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
